import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";

import { buildShareUrl } from "common/shareUrl";
import {
  buildSnapshot,
  FIELD_DEFS,
  isEmptyValue,
  ShareContext,
  ShareFieldKey,
} from "common/taskSnapshot";
import { useSelectedOrganization, useSnackbar, useTranslations } from "hooks";
import taskShareApi from "api/taskShareApi";
import { Task } from "model/activitiesModel";
import { Attachment } from "components/AttachmentList";
import { CompleteComment } from "components/CommentsSection";
import { HistoryEntry } from "components/HistoriesSection";
import { Album } from "model/albumModel";

/**
 * Condivisione di un task con un link pubblico.
 *
 * Il link punta alla pagina read-only `SharedTaskPage` (`/#/tasks/shared/<hash>`),
 * che legge lo snapshot **congelato** dal backend: quello che l'utente vede è
 * esattamente quello che è stato selezionato qui, non lo stato attuale del task.
 *
 * Lo snapshot lo costruisce il client (il task è già in memoria) e il server
 * scarta le chiavi fuori dalla sua whitelist (`SHAREABLE_FIELDS` in
 * backend_statistiche/app/services/task_share_service.py): gli id interni e gli
 * allegati non possono uscire, indipendentemente da cosa manda il client.
 *
 * Il path `tasks/shared` è lo stesso sul BE e sulla rotta SPA (vedi
 * `buildShareUrl`).
 */
const SHARE_PATH = "tasks/shared";

/** Copia negli appunti con il fallback per i browser/contesti senza clipboard API. */
const copyToClipboard = (text: string): Promise<void> => {
  const fallback = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch {
      /* niente: l'utente ha comunque il campo di testo selezionabile */
    }
  };
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(fallback);
  }
  fallback();
  return Promise.resolve();
};

interface TaskShareLinkDialogProps {
  open: boolean;
  onClose: () => void;
  task?: Task;
  /** Allegati, commenti e storico del task: li passa TaskDetailDialog, che li ha
   *  già in stato (nessun fetch aggiuntivo per costruire lo snapshot). */
  attachments?: Attachment[];
  comments?: CompleteComment[];
  history?: HistoryEntry[];
  albums?: Album[];
}

const TaskShareLinkDialog: React.FC<TaskShareLinkDialogProps> = ({
  open,
  onClose,
  task,
  attachments,
  comments,
  history,
  albums,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const organization = useSelectedOrganization();

  const [selected, setSelected] = useState<Set<ShareFieldKey>>(
    () => new Set(FIELD_DEFS.filter((d) => d.defaultOn).map((d) => d.key))
  );
  const [url, setUrl] = useState<string>("");
  // `null` = tutti gli album (`albums` può arrivare dopo l'apertura: meglio un
  // default "tutto" che un set vuoto che condivide zero album).
  const [albumIds, setAlbumIds] = useState<Set<number> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const linkRef = useRef<HTMLDivElement>(null);

  const t = useCallback(
    // `getTranslation` ritorna la chiave stessa se manca, quindi `|| fallback`
    // non scatterebbe mai: qui il fallback è vero.
    (key: string, fallback: string) => translations?.[key] || fallback,
    [translations]
  );

  // Riaprire la dialog ricomincia dalla selezione di default: la condivisione
  // precedente non viene riletta dal server (il POST è idempotente e riusa
  // l'hash, quindi ricondividere non rompe il link già girato).
  useEffect(() => {
    if (open) {
      setSelected(new Set(FIELD_DEFS.filter((d) => d.defaultOn).map((d) => d.key)));
      setAlbumIds(null);
      setUrl("");
      setError("");
      setCopied(false);
    }
  }, [open]);

  // Appena il link è pronto lo si porta in vista: è quello che l'utente è
  // venuto a prendere.
  useEffect(() => {
    if (url) linkRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [url]);

  // Un solo contesto per preview, conteggi e POST: non possono divergere.
  const shareCtx = useMemo<ShareContext>(
    () => ({ attachments, comments, history, albums, albumIds: albumIds ?? undefined }),
    [attachments, comments, history, albums, albumIds]
  );

  const previewCount = useMemo(
    () => (task ? Object.keys(buildSnapshot(task, selected, shareCtx)).length : 0),
    [task, selected, shareCtx]
  );

  const toggle = (key: ShareFieldKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleShare = async () => {
    if (!task || !organization?.id) return;
    const fields = buildSnapshot(task, selected, shareCtx);
    if (Object.keys(fields).length === 0) {
      setError(t("task_share.nothing_selected", "Seleziona almeno un dato da condividere."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await taskShareApi.post(`/${task.idprocesstoken}/share`, {
        idorganization: organization.id,
        shared: true,
        fields,
      });
      const payload = data?.data;
      if (!payload?.url) {
        setError(t("task_share.failed", "Condivisione non riuscita."));
        return;
      }
      setUrl(buildShareUrl(payload.url, SHARE_PATH));
      sendSnack({
        type: "success",
        message: t("task_share.created", "Link di condivisione pronto."),
      });
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          t("task_share.failed", "Condivisione non riuscita.")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!task) return;
    setBusy(true);
    setError("");
    try {
      await taskShareApi.post(`/${task.idprocesstoken}/share`, {
        idorganization: organization?.id,
        shared: false,
      });
      setUrl("");
      setCopied(false);
      sendSnack({
        type: "success",
        message: t("task_share.revoked", "Link revocato: non è più raggiungibile."),
      });
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          t("task_share.failed", "Condivisione non riuscita.")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    await copyToClipboard(url);
    setCopied(true);
    sendSnack({
      type: "success",
      message: t("task_share.copied", "Link copiato negli appunti."),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LinkIcon fontSize="small" />
        {t("task_share.title", "Condividi il task con un link")}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            "task_share.description",
            "Scegli quali dati finiscono nel link. Il contenuto viene congelato adesso: chi apre il link vedrà questa selezione, non gli aggiornamenti successivi del task."
          )}
        </Typography>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">
            {t("task_share.fields", "Dati da condividere")}
          </Typography>
          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Checkbox
                size="small"
                id="task-share-select-all"
                checked={selected.size === FIELD_DEFS.length}
                onChange={(e) =>
                  setSelected(
                    e.target.checked ? new Set(FIELD_DEFS.map((d) => d.key)) : new Set()
                  )
                }
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("generic.select.all", "Seleziona tutti")}
              </Typography>
            }
          />
        </Stack>
        <Stack>
          {FIELD_DEFS.map((def) => {
            const value = task ? def.build(task, shareCtx) : null;
            const empty = isEmptyValue(value);
            // Il conteggio sulle voci "di elenco" dice subito quanto si sta per
            // condividere ("4 allegati", "12 commenti"): stessa forma "(N)" che
            // il dialog usa nell'header dell'accordion Allegati. Per gli album
            // conta quelli spuntati, non tutti.
            const count = Array.isArray(value) ? value.length : undefined;
            const showAlbumPicker =
              def.key === "albums" && selected.has("albums") && (albums?.length ?? 0) > 1;
            return (
              <Fragment key={def.key}>
                <FormControlLabel
                  sx={{ ml: 0, mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      id={`task-share-field-${def.key}`}
                      checked={selected.has(def.key)}
                      onChange={() => toggle(def.key)}
                    />
                  }
                  label={
                    <Typography variant="body2" color={empty ? "text.disabled" : "text.primary"}>
                      {t(def.labelKey, def.label)}
                      {count !== undefined ? ` (${count})` : ""}
                    </Typography>
                  }
                />
                {showAlbumPicker && (
                  <Stack sx={{ pl: 4 }}>
                    {albums!.map((al) => (
                      <FormControlLabel
                        key={al.id}
                        sx={{ ml: 0, mr: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            id={`task-share-album-${al.id}`}
                            checked={albumIds ? albumIds.has(al.id) : true}
                            onChange={() =>
                              setAlbumIds((prev) => {
                                const all = new Set((albums ?? []).map((a) => a.id));
                                const next = new Set(prev ?? all);
                                if (next.has(al.id)) next.delete(al.id);
                                else next.add(al.id);
                                return next;
                              })
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {al.name}
                            {` (${al.images.length})`}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                )}
              </Fragment>
            );
          })}
        </Stack>

        {url && (
          <Box ref={linkRef}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("task_share.link", "Link da girare")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                id="task-share-link"
                value={url}
                fullWidth
                size="small"
                InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: "0.8rem" } }}
                onFocus={(e) => e.target.select()}
              />
              <Tooltip
                title={
                  copied
                    ? t("task_share.copied", "Link copiato negli appunti.")
                    : t("task_share.copy", "Copia il link")
                }
              >
                <IconButton onClick={handleCopy} size="small" id="task-share-copy">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t(
                "task_share.public_note",
                "Chiunque abbia questo link può vederlo, senza fare login. Puoi revocarlo in qualsiasi momento."
              )}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleRevoke}
          disabled={busy}
          color="inherit"
          id="task-share-revoke"
        >
          {t("task_share.revoke", "Revoca il link")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} color="inherit">
          {t("pm.minimap.close.lable", "Chiudi")}
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          disabled={busy || previewCount === 0}
          id="task-share-confirm"
        >
          {busy ? <CircularProgress size={18} color="inherit" /> : t("task_share.confirm", "Crea il link")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskShareLinkDialog;
